import { Request, Response } from "express";
import mongoose from "mongoose";

import { Class, Semester, Student, Teacher } from "../models";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

class SemesterEnrollmentController {
  /**
   * Get all school semesters with enrollment statistics
   * @route GET /api/v1/school/semesters/enrollment
   * @access Private (School Admin)
   */
  async getAllSemestersWithEnrollment(
    req: Request,
    res: Response,
  ): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const schoolId = authReq.user?.schoolId;
      const {
        academicYear,
        status,
        isActive,
        page = 1,
        limit = 10,
        sortBy = "startDate",
        sortOrder = "desc",
      } = req.query;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      // Build query
      const queryFilter: any = {
        schoolId: new mongoose.Types.ObjectId(schoolId),
      };

      if (academicYear) queryFilter.academicYear = academicYear;
      if (status) queryFilter.status = status;
      if (isActive !== undefined) queryFilter.isActive = isActive === "true";

      // Build sort object
      const sort: any = {};
      sort[sortBy as string] = sortOrder === "desc" ? -1 : 1;

      // Calculate pagination
      const skip = (Number(page) - 1) * Number(limit);

      const [semesters, totalCount] = await Promise.all([
        Semester.find({ ...queryFilter })
          .sort(sort)
          .skip(skip)
          .limit(Number(limit))
          .populate("createdBy", "fullName email"),
        Semester.countDocuments({ ...queryFilter }),
      ]);

      // Get enrollment statistics for each semester
      const semestersWithStats = await Promise.all(
        semesters.map(async semester => {
          // Get classes in this semester
          const classes = await Class.find({
            semesterId: semester._id,
            schoolId: new mongoose.Types.ObjectId(schoolId),
          })
            .populate("teacherIds", "fullName email subjects")
            .populate("studentIds", "fullName studentId profilePicture");

          // Calculate statistics
          const totalClasses = classes.length;
          const activeClasses = classes.filter(
            c => c.status === "active",
          ).length;
          const totalStudents = classes.reduce(
            (sum, c) => sum + (c.studentIds?.length || 0),
            0,
          );
          const totalTeachers = new Set(
            classes
              .flatMap(c => (c.teacherIds || []).map(t => t.toString()))
              .filter(Boolean),
          ).size;

          // Get unique students across all classes
          const uniqueStudents = new Set();
          for (const c of classes) {
            c.studentIds?.forEach((student: any) => {
              uniqueStudents.add(student._id.toString());
            });
          }

          // Determine semester status
          const now = new Date();
          let semesterStatus = "upcoming";
          if (now >= semester.startDate && now <= semester.endDate) {
            semesterStatus = "current";
          } else if (now > semester.endDate) {
            semesterStatus = "completed";
          }

          return {
            id: semester._id,
            semesterName: semester.semesterName,
            academicYear: semester.academicYear,
            startDate: semester.startDate,
            endDate: semester.endDate,
            status: semester.status,
            isActive: semester.isActive,
            description: semester.description,
            createdBy: semester.createdBy,
            createdAt: semester.createdAt,
            updatedAt: semester.updatedAt,
            // Enrollment statistics
            statistics: {
              totalClasses,
              activeClasses,
              totalStudents: uniqueStudents.size,
              totalTeachers,
              enrollmentRate:
                totalClasses > 0
                  ? Math.round((activeClasses / totalClasses) * 100)
                  : 0,
            },
            // Semester timeline status
            timelineStatus: semesterStatus,
            isCurrent: semester.isCurrentSemester(),
            hasStarted: semester.hasStarted(),
            hasEnded: semester.hasEnded(),
          };
        }),
      );

      const totalPages = Math.ceil(totalCount / Number(limit));
      const hasNextPage = Number(page) < totalPages;
      const hasPrevPage = Number(page) > 1;

      res.status(200).json({
        success: true,
        message: "Semesters retrieved successfully",
        data: {
          semesters: semestersWithStats,
          pagination: {
            currentPage: Number(page),
            totalPages,
            totalCount,
            hasNextPage,
            hasPrevPage,
            limit: Number(limit),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get semesters with enrollment error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get semester courses with enrollment details
   * @route GET /api/v1/school/semesters/:id/courses
   * @access Private (School Admin)
   */
  async getSemesterCourses(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const { id } = req.params;
      const schoolId = authReq.user?.schoolId;
      const {
        status,
        subject,
        teacherId,
        page = 1,
        limit = 10,
        sortBy = "className",
        sortOrder = "asc",
      } = req.query;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid semester ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Verify semester exists and belongs to school
      const semester = await Semester.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!semester) {
        res.status(404).json({
          success: false,
          message: "Semester not found",
          error: "Semester not found",
        } as APIResponse);
        return;
      }

      // Build class query
      const classFilter: any = {
        semesterId: new mongoose.Types.ObjectId(id),
        schoolId: new mongoose.Types.ObjectId(schoolId),
      };

      if (status) classFilter.status = status;
      if (subject) classFilter.subject = new RegExp(subject as string, "i");
      if (teacherId)
        classFilter.teacherId = new mongoose.Types.ObjectId(
          teacherId as string,
        );

      // Build sort object
      const sort: any = {};
      sort[sortBy as string] = sortOrder === "desc" ? -1 : 1;

      // Calculate pagination
      const skip = (Number(page) - 1) * Number(limit);

      const [classes, totalCount] = await Promise.all([
        Class.find({ ...classFilter })
          .sort(sort)
          .skip(skip)
          .limit(Number(limit))
          .populate("teacherIds", "fullName email subjects profilePicture")
          .populate("studentIds", "fullName studentId profilePicture")
          .populate("createdBy", "fullName email"),
        Class.countDocuments({ ...classFilter }),
      ]);

      // Format classes with enrollment details
      const formattedClasses = classes.map(classItem => ({
        id: classItem._id,
        className: classItem.className,
        classCode: classItem.classCode,
        subject: classItem.subject,
        description: classItem.description,
        status: classItem.status,
        isActive: classItem.isActive,
        maxStudents: classItem.maxStudents,
        currentStudents: classItem.studentIds?.length || 0,
        enrollmentPercentage: classItem.maxStudents
          ? Math.round(
              ((classItem.studentIds?.length || 0) / classItem.maxStudents) *
                100,
            )
          : 0,
        isFull: classItem.isFull,
        schedule: classItem.schedule,
        classRoom: classItem.classRoom,
        settings: classItem.settings,
        teacher:
          classItem.teacherIds && classItem.teacherIds.length > 0
            ? {
                id: (classItem.teacherIds[0] as any)._id,
                fullName: (classItem.teacherIds[0] as any).fullName,
                email: (classItem.teacherIds[0] as any).email,
                subjects: (classItem.teacherIds[0] as any).subjects,
                profilePicture: (classItem.teacherIds[0] as any).profilePicture,
              }
            : null,
        students:
          (classItem.studentIds as any[])?.map(student => ({
            id: student._id,
            fullName: student.fullName,
            studentId: student.studentId,
            profilePicture: student.profilePicture,
          })) || [],
        createdBy: classItem.createdBy,
        createdAt: classItem.createdAt,
        updatedAt: classItem.updatedAt,
      }));

      const totalPages = Math.ceil(totalCount / Number(limit));
      const hasNextPage = Number(page) < totalPages;
      const hasPrevPage = Number(page) > 1;

      res.status(200).json({
        success: true,
        message: "Semester courses retrieved successfully",
        data: {
          semester: {
            id: semester._id,
            semesterName: semester.semesterName,
            academicYear: semester.academicYear,
            startDate: semester.startDate,
            endDate: semester.endDate,
            status: semester.status,
            isActive: semester.isActive,
            timelineStatus: (() => {
              if (semester.hasEnded()) return "completed";
              if (semester.hasStarted()) return "current";
              return "upcoming";
            })(),
          },
          courses: formattedClasses,
          pagination: {
            currentPage: Number(page),
            totalPages,
            totalCount,
            hasNextPage,
            hasPrevPage,
            limit: Number(limit),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get semester courses error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get semester enrollment summary
   * @route GET /api/v1/school/semesters/:id/enrollment-summary
   * @access Private (School Admin)
   */
  async getSemesterEnrollmentSummary(
    req: Request,
    res: Response,
  ): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const { id } = req.params;
      const schoolId = authReq.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid semester ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Verify semester exists
      const semester = await Semester.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!semester) {
        res.status(404).json({
          success: false,
          message: "Semester not found",
          error: "Semester not found",
        } as APIResponse);
        return;
      }

      // Get all classes in this semester
      const classes = await Class.find({
        semesterId: new mongoose.Types.ObjectId(id),
        schoolId: new mongoose.Types.ObjectId(schoolId),
      })
        .populate("teacherIds", "fullName email subjects")
        .populate("studentIds", "fullName studentId profilePicture");

      // Calculate comprehensive statistics
      const totalClasses = classes.length;
      const activeClasses = classes.filter(c => c.status === "active").length;
      const completedClasses = classes.filter(
        c => c.status === "completed",
      ).length;
      const cancelledClasses = classes.filter(
        c => c.status === "cancelled",
      ).length;

      // Get unique students and teachers
      const uniqueStudents = new Set();
      const uniqueTeachers = new Set();
      const subjectStats: Record<string, number> = {};

      for (const classItem of classes) {
        // Count students
        classItem.studentIds?.forEach((student: any) => {
          uniqueStudents.add(student._id.toString());
        });

        // Count teachers
        if (classItem.teacherIds && classItem.teacherIds.length > 0) {
          for (const teacher of classItem.teacherIds) {
            uniqueTeachers.add((teacher as any)._id.toString());
          }
        }

        // Count subjects
        if (classItem.subject) {
          subjectStats[classItem.subject] =
            (subjectStats[classItem.subject] || 0) + 1;
        }
      }

      // Calculate enrollment rates
      const totalCapacity = classes.reduce(
        (sum, c) => sum + (c.maxStudents || 0),
        0,
      );
      const totalEnrolled = classes.reduce(
        (sum, c) => sum + (c.studentIds?.length || 0),
        0,
      );
      const enrollmentRate =
        totalCapacity > 0
          ? Math.round((totalEnrolled / totalCapacity) * 100)
          : 0;

      // Get top subjects
      const topSubjects = Object.entries(subjectStats)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([subject, count]) => ({ subject, classCount: count }));

      // Determine semester timeline status
      const now = new Date();
      let timelineStatus = "upcoming";
      if (now >= semester.startDate && now <= semester.endDate) {
        timelineStatus = "current";
      } else if (now > semester.endDate) {
        timelineStatus = "completed";
      }

      res.status(200).json({
        success: true,
        message: "Semester enrollment summary retrieved successfully",
        data: {
          semester: {
            id: semester._id,
            semesterName: semester.semesterName,
            academicYear: semester.academicYear,
            startDate: semester.startDate,
            endDate: semester.endDate,
            status: semester.status,
            isActive: semester.isActive,
            timelineStatus,
            isCurrent: semester.isCurrentSemester(),
            hasStarted: semester.hasStarted(),
            hasEnded: semester.hasEnded(),
          },
          statistics: {
            classes: {
              total: totalClasses,
              active: activeClasses,
              completed: completedClasses,
              cancelled: cancelledClasses,
            },
            enrollment: {
              totalStudents: uniqueStudents.size,
              totalTeachers: uniqueTeachers.size,
              totalCapacity,
              totalEnrolled,
              enrollmentRate,
            },
            subjects: {
              total: Object.keys(subjectStats).length,
              topSubjects,
            },
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get semester enrollment summary error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get current semester with active enrollments
   * @route GET /api/v1/school/semesters/current
   * @access Private (School Admin)
   */
  async getCurrentSemester(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const schoolId = authReq.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      // Find current semester
      const currentSemester = await Semester.findCurrent(
        new mongoose.Types.ObjectId(schoolId),
      );

      if (!currentSemester) {
        res.status(404).json({
          success: false,
          message: "No current semester found",
          error: "No active semester",
        } as APIResponse);
        return;
      }

      // Get classes in current semester
      const classes = await Class.find({
        semesterId: currentSemester._id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      })
        .populate("teacherIds", "fullName email subjects")
        .populate("studentIds", "fullName studentId profilePicture");

      // Calculate quick stats
      const totalClasses = classes.length;
      const activeClasses = classes.filter(c => c.status === "active").length;
      const totalStudents = new Set(
        classes.flatMap(
          c => c.studentIds?.map((s: any) => s._id.toString()) || [],
        ),
      ).size;
      const totalTeachers = new Set(
        classes
          .flatMap(c => (c.teacherIds || []).map(t => t.toString()))
          .filter(Boolean),
      ).size;

      res.status(200).json({
        success: true,
        message: "Current semester retrieved successfully",
        data: {
          semester: {
            id: currentSemester._id,
            semesterName: currentSemester.semesterName,
            academicYear: currentSemester.academicYear,
            startDate: currentSemester.startDate,
            endDate: currentSemester.endDate,
            status: currentSemester.status,
            isActive: currentSemester.isActive,
            timelineStatus: "current",
            isCurrent: true,
            hasStarted: currentSemester.hasStarted(),
            hasEnded: currentSemester.hasEnded(),
          },
          quickStats: {
            totalClasses,
            activeClasses,
            totalStudents,
            totalTeachers,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get current semester error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get past semesters (completed)
   * @route GET /api/v1/school/semesters/past
   * @access Private (School Admin)
   */
  async getPastSemesters(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const schoolId = authReq.user?.schoolId;
      const {
        academicYear,
        page = 1,
        limit = 10,
        sortBy = "endDate",
        sortOrder = "desc",
      } = req.query;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      // Build query for past semesters
      const now = new Date();
      const query: any = {
        schoolId: new mongoose.Types.ObjectId(schoolId),
        endDate: { $lt: now }, // Semesters that have ended
      };

      if (academicYear) query.academicYear = academicYear;

      // Build sort object
      const sort: any = {};
      sort[sortBy as string] = sortOrder === "desc" ? -1 : 1;

      // Calculate pagination
      const skip = (Number(page) - 1) * Number(limit);

      const [semesters, totalCount] = await Promise.all([
        Semester.find({ ...query })
          .sort(sort)
          .skip(skip)
          .limit(Number(limit))
          .populate("createdBy", "fullName email"),
        Semester.countDocuments({ ...query }),
      ]);

      // Get enrollment statistics for each past semester
      const pastSemestersWithStats = await Promise.all(
        semesters.map(async semester => {
          const classes = await Class.find({
            semesterId: semester._id,
            schoolId: new mongoose.Types.ObjectId(schoolId),
          });

          const totalClasses = classes.length;
          const completedClasses = classes.filter(
            c => c.status === "completed",
          ).length;
          const totalStudents = classes.reduce(
            (sum, c) => sum + (c.studentIds?.length || 0),
            0,
          );
          const totalTeachers = new Set(
            classes
              .flatMap(c => (c.teacherIds || []).map(t => t.toString()))
              .filter(Boolean),
          ).size;

          return {
            id: semester._id,
            semesterName: semester.semesterName,
            academicYear: semester.academicYear,
            startDate: semester.startDate,
            endDate: semester.endDate,
            status: semester.status,
            isActive: semester.isActive,
            description: semester.description,
            createdBy: semester.createdBy,
            createdAt: semester.createdAt,
            updatedAt: semester.updatedAt,
            statistics: {
              totalClasses,
              completedClasses,
              totalStudents,
              totalTeachers,
              completionRate:
                totalClasses > 0
                  ? Math.round((completedClasses / totalClasses) * 100)
                  : 0,
            },
            timelineStatus: "completed",
            isCurrent: false,
            hasStarted: true,
            hasEnded: true,
          };
        }),
      );

      const totalPages = Math.ceil(totalCount / Number(limit));
      const hasNextPage = Number(page) < totalPages;
      const hasPrevPage = Number(page) > 1;

      res.status(200).json({
        success: true,
        message: "Past semesters retrieved successfully",
        data: {
          semesters: pastSemestersWithStats,
          pagination: {
            currentPage: Number(page),
            totalPages,
            totalCount,
            hasNextPage,
            hasPrevPage,
            limit: Number(limit),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get past semesters error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }
}

export const semesterEnrollmentController = new SemesterEnrollmentController();
